import React from 'react';
import {
  ChartBarIcon,
  DocumentTextIcon,
  ShieldCheckIcon,
  CubeIcon,
  ArrowDownTrayIcon,
} from '@heroicons/react/24/outline';
import { HealthDashboard as AnalyticsDashboard } from '../../api/healthAnalytics';

interface HealthReportsPanelProps {
  analyticsDashboard: AnalyticsDashboard | null;
  onOpenReportModal: () => void;
  onGenerateReport: (type: 'bird' | 'flock' | 'analytics' | 'vaccination' | 'treatment') => void;
}

const reportCards = [
  {
    id: 'flock' as const,
    title: 'Flock health report',
    description: 'Summary across all birds — status, tests, and trends.',
    icon: ChartBarIcon,
    color: 'text-green-600 bg-green-50',
  },
  {
    id: 'analytics' as const,
    title: 'Health analytics',
    description: 'Scores, checklist trends, and recommendations.',
    icon: ChartBarIcon,
    color: 'text-purple-600 bg-purple-50',
  },
  {
    id: 'vaccination' as const,
    title: 'Vaccination report',
    description: 'Scheduled, overdue, and completed vaccinations.',
    icon: ShieldCheckIcon,
    color: 'text-cyan-600 bg-cyan-50',
  },
  {
    id: 'treatment' as const,
    title: 'Treatment report',
    description: 'Active and completed treatment records.',
    icon: CubeIcon,
    color: 'text-amber-600 bg-amber-50',
  },
];

export default function HealthReportsPanel({
  analyticsDashboard,
  onOpenReportModal,
  onGenerateReport,
}: HealthReportsPanelProps) {
  return (
    <div className="space-y-6">
      {analyticsDashboard && (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <div className="rounded-lg bg-white p-4 shadow-sm ring-1 ring-gray-200">
            <p className="text-sm text-slate-500">Total birds</p>
            <p className="text-2xl font-semibold">{analyticsDashboard.overview?.totalBirds ?? '—'}</p>
          </div>
          <div className="rounded-lg bg-white p-4 shadow-sm ring-1 ring-gray-200">
            <p className="text-sm text-slate-500">Healthy</p>
            <p className="text-2xl font-semibold text-green-600">{analyticsDashboard.overview?.healthyBirds ?? '—'}</p>
          </div>
          <div className="rounded-lg bg-white p-4 shadow-sm ring-1 ring-gray-200">
            <p className="text-sm text-slate-500">Under observation</p>
            <p className="text-2xl font-semibold text-amber-600">{analyticsDashboard.overview?.birdsUnderObservation ?? '—'}</p>
          </div>
          <div className="rounded-lg bg-white p-4 shadow-sm ring-1 ring-gray-200">
            <p className="text-sm text-slate-500">Health concerns</p>
            <p className="text-2xl font-semibold text-red-600">{analyticsDashboard.overview?.birdsWithHealthConcerns ?? '—'}</p>
          </div>
        </div>
      )}

      {analyticsDashboard?.recommendations && analyticsDashboard.recommendations.length > 0 && (
        <div className="rounded-lg border border-cyan-100 bg-cyan-50/50 p-4">
          <h3 className="text-sm font-semibold text-slate-900">Recommendations</h3>
          <ul className="mt-2 space-y-2">
            {analyticsDashboard.recommendations.slice(0, 5).map((rec) => (
              <li key={rec.id} className="text-sm text-slate-700">
                <span className="font-medium">{rec.title}:</span> {rec.description}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-slate-900">Generate reports</h3>
          <p className="text-sm text-slate-500">Download PDF or CSV reports for records and compliance.</p>
        </div>
        <button type="button" onClick={onOpenReportModal} className="btn btn-secondary text-sm">
          <DocumentTextIcon className="h-4 w-4" />
          Custom report
        </button>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        {reportCards.map((card) => (
          <button
            key={card.id}
            type="button"
            onClick={() => onGenerateReport(card.id)}
            className="flex items-start gap-4 rounded-xl border border-gray-200 bg-white p-5 text-left transition hover:border-cyan-300 hover:shadow-md"
          >
            <div className={`rounded-lg p-2.5 ${card.color}`}>
              <card.icon className="h-6 w-6" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-medium text-slate-900">{card.title}</p>
              <p className="mt-1 text-sm text-slate-500">{card.description}</p>
              <span className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-cyan-600">
                <ArrowDownTrayIcon className="h-4 w-4" />
                Generate
              </span>
            </div>
          </button>
        ))}
      </div>

      <p className="text-sm text-slate-500">
        For individual bird reports, open a bird&apos;s profile and use the Health Records tab, or use Custom report to select format and type.
      </p>
    </div>
  );
}
